import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Chip,
  Grid,
  Alert,
  alpha,
  useTheme,
} from "@mui/material";
import PaypalIcon from "@mui/icons-material/AccountBalanceWallet";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaymentIcon from "@mui/icons-material/Payment";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import PageHeader from "../../common/PageHeader";

const GatewayCard = ({
  title,
  icon: Icon,
  color,
  isActive,
  onToggle,
  children,
  onSave,
  saving,
}) => {
  const theme = useTheme();
  return (
    <Card
      sx={{
        border: `1px solid ${alpha(color, isActive ? 0.3 : 0.1)}`,
        transition: "all 0.2s ease",
        "&:hover": { borderColor: alpha(color, 0.3) },
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: isActive
            ? `linear-gradient(90deg, ${color}, ${alpha(color, 0.5)})`
            : "transparent",
          transition: "all 0.3s ease",
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={2.5}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: alpha(color, 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Icon sx={{ color, fontSize: 20 }} />
            </Box>
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle1" fontWeight={700}>
                  {title}
                </Typography>
                <Chip
                  label={isActive ? "Active" : "Inactive"}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    bgcolor: isActive
                      ? alpha(color, 0.12)
                      : alpha(theme.palette.text.disabled, 0.1),
                    color: isActive ? color : "text.disabled",
                  }}
                />
              </Stack>
            </Box>
          </Stack>
          <FormControlLabel
            control={
              <Switch
                checked={!!isActive}
                onChange={onToggle}
                size="small"
                sx={{
                  "& .MuiSwitch-thumb": {
                    bgcolor: isActive ? color : undefined,
                  },
                }}
              />
            }
            label={
              <Typography variant="caption" fontWeight={600}>
                {isActive ? "Enabled" : "Disabled"}
              </Typography>
            }
            labelPlacement="start"
          />
        </Stack>
        <Divider sx={{ mb: 2.5 }} />
        {children}
        <Box mt={2.5}>
          <Button
            variant="contained"
            onClick={onSave}
            sx={{
              borderRadius: 2,
              bgcolor: color,
              "&:hover": { bgcolor: alpha(color, 0.85) },
            }}
          >
            Save {title}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
};

const PaymentGateway = () => {
  const { hitAxios } = React.useContext(GlobalContext);
  const { lang } = React.useContext(TranslateContext);
  const [paypal, setPaypal] = useState({
    client_id: "",
    client_secret: "",
    mode: "sandbox",
    is_active: false,
  });
  const [stripe, setStripe] = useState({
    client_id: "",
    client_secret: "",
    mode: "sandbox",
    is_active: false,
  });
  const [offline, setOffline] = useState({ is_active: false });
  const [offlineSettings, setOfflineSettings] = useState({
    offline_payment_instructions: "",
    offline_payment_details: "",
  });

  useEffect(() => {
    loadGateways();
    loadOfflineSettings();
  }, []);

  const loadGateways = async () => {
    const res = await hitAxios({
      path: "/api/settings/payment-gateway",
      admin: true,
      showSnackbar: false,
    });
    if (res?.data?.success) {
      const pp = res.data.data.find((g) => g.gateway === "paypal");
      const st = res.data.data.find((g) => g.gateway === "stripe");
      const of = res.data.data.find((g) => g.gateway === "offline");
      if (pp) setPaypal({ ...pp, is_active: pp.is_active === 1 });
      if (st) setStripe({ ...st, is_active: st.is_active === 1 });
      if (of) setOffline({ ...of, is_active: of.is_active === 1 });
    }
  };
  const loadOfflineSettings = async () => {
    const res = await hitAxios({
      path: "/api/settings/get-all",
      showSnackbar: false,
    });
    if (res?.data?.success)
      setOfflineSettings({
        offline_payment_instructions:
          res.data.data.offline_payment_instructions || "",
        offline_payment_details: res.data.data.offline_payment_details || "",
      });
  };
  const saveGateway = async (gateway, data) => {
    await hitAxios({
      path: "/api/settings/update-payment-gateway",
      post: true,
      admin: true,
      obj: { ...data, gateway },
    });
  };
  const saveOfflineSettings = async () => {
    await hitAxios({
      path: "/api/settings/update",
      post: true,
      admin: true,
      obj: offlineSettings,
    });
  };

  return (
    <Box>
      <PageHeader
        title={lang.paymentGateway || "Payment Gateway"}
        subtitle="Configure payment methods for your users"
        icon={PaymentIcon}
      />

      <Stack spacing={3}>
        <GatewayCard
          title="PayPal"
          icon={PaypalIcon}
          color="#0070BA"
          isActive={paypal.is_active}
          onToggle={(e) =>
            setPaypal({ ...paypal, is_active: e.target.checked })
          }
          onSave={() => saveGateway("paypal", paypal)}
        >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="PayPal Client ID"
                value={paypal.client_id}
                onChange={(e) =>
                  setPaypal({ ...paypal, client_id: e.target.value })
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="password"
                label="PayPal Client Secret"
                value={paypal.client_secret}
                onChange={(e) =>
                  setPaypal({ ...paypal, client_secret: e.target.value })
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Mode</InputLabel>
                <Select
                  value={paypal.mode}
                  onChange={(e) =>
                    setPaypal({ ...paypal, mode: e.target.value })
                  }
                  label="Mode"
                >
                  <MenuItem value="sandbox">Sandbox (Testing)</MenuItem>
                  <MenuItem value="live">Live (Production)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </GatewayCard>

        <GatewayCard
          title="Stripe"
          icon={CreditCardIcon}
          color="#635BFF"
          isActive={stripe.is_active}
          onToggle={(e) =>
            setStripe({ ...stripe, is_active: e.target.checked })
          }
          onSave={() => saveGateway("stripe", stripe)}
        >
          <Alert
            severity="info"
            sx={{ mb: 2, borderRadius: 2, fontSize: "0.8rem" }}
          >
            Get your keys from{" "}
            <a
              href="https://dashboard.stripe.com/apikeys"
              target="_blank"
              rel="noreferrer"
              style={{ color: "#635BFF" }}
            >
              dashboard.stripe.com/apikeys
            </a>
          </Alert>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Publishable Key (pk_...)"
                value={stripe.client_id}
                placeholder="pk_test_..."
                onChange={(e) =>
                  setStripe({ ...stripe, client_id: e.target.value })
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="password"
                label="Secret Key (sk_...)"
                value={stripe.client_secret}
                placeholder="sk_test_..."
                onChange={(e) =>
                  setStripe({ ...stripe, client_secret: e.target.value })
                }
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Mode</InputLabel>
                <Select
                  value={stripe.mode}
                  onChange={(e) =>
                    setStripe({ ...stripe, mode: e.target.value })
                  }
                  label="Mode"
                >
                  <MenuItem value="sandbox">Test Mode</MenuItem>
                  <MenuItem value="live">Live Mode</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </GatewayCard>

        <GatewayCard
          title="Offline Payment"
          icon={ReceiptLongIcon}
          color="#10B981"
          isActive={offline.is_active}
          onToggle={(e) =>
            setOffline({ ...offline, is_active: e.target.checked })
          }
          onSave={() => {
            saveGateway("offline", offline);
            saveOfflineSettings();
          }}
        >
          <Alert
            severity="info"
            sx={{ mb: 2, borderRadius: 2, fontSize: "0.8rem" }}
          >
            Users will see these instructions when choosing offline payment.
            Confirm payments manually from the Orders page.
          </Alert>
          <Grid container spacing={2}>
            <Grid size={12}>
              <TextField
                fullWidth
                size="small"
                label="Payment Instructions"
                multiline
                rows={3}
                value={offlineSettings.offline_payment_instructions}
                onChange={(e) =>
                  setOfflineSettings({
                    ...offlineSettings,
                    offline_payment_instructions: e.target.value,
                  })
                }
                placeholder="e.g. Please transfer the amount to our bank account..."
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth
                size="small"
                label="Payment Details (Bank/UPI/Crypto etc)"
                multiline
                rows={4}
                value={offlineSettings.offline_payment_details}
                onChange={(e) =>
                  setOfflineSettings({
                    ...offlineSettings,
                    offline_payment_details: e.target.value,
                  })
                }
                placeholder={
                  "Bank: Example Bank\nAccount: 1234567890\nIFSC: EXMP0001234"
                }
                sx={{
                  "& textarea": { fontFamily: "monospace", fontSize: "0.8rem" },
                }}
              />
            </Grid>
          </Grid>
        </GatewayCard>
      </Stack>
    </Box>
  );
};

export default PaymentGateway;
